import axios from "axios";
import { router } from "../main"; // Asegúrate de que la ruta a tu main.js es correcta
import Swal from "sweetalert2";
import i18n from "../i18n"; // Ajusta la ruta según tu configuración
import { resolveWencesApiBaseUrl } from "@/utils/tenantTestEnvironment";
import {
  isModelDefinitionsListUrl,
  getModelDefinitionName,
  findInCachedModelDefinitions,
  deriveModelDefinitionFromList,
  readCachedModelDefinitions,
  writeCachedModelDefinitions,
  clearModelDefinitionCache,
  getInflightModelDefinitions,
  markInflightModelDefinitions,
  settleInflightModelDefinitions,
} from "@/utils/modelDefinitionCache";
import {
  getDedupeKey,
  getInflightRequest,
  markInflightRequest,
  settleInflightRequest,
} from "@/utils/inflightRequestDedupe";

// Cache simple para almacenar respuestas de API
const apiCache = new Map();

// Configuración del caché
const CACHE_DURATION = 2000; // 2 segundos - se limpia automáticamente después de PUT/POST/DELETE

// Limpiar caché al inicio para pruebas
apiCache.clear();

/** Front o API apuntando a localhost (entorno de prueba local). */
export function isLocalTestEnvironment() {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host === "::1") {
    return true;
  }
  const apiBase = getWencesApiBaseUrl();
  try {
    const { hostname } = new URL(apiBase);
    return hostname === "localhost" || hostname === "127.0.0.1";
  } catch {
    return apiBase.includes("localhost") || apiBase.includes("127.0.0.1");
  }
}

/** Nombre de BD / tenant actual (misma lógica que cabecera X-Subdomain). */
export function getCurrentClientDbName() {
  const hostname = window.location.hostname;
  if (hostname.endsWith(".delegat.es") || hostname.endsWith(".delegat.test") || hostname.endsWith(".iajur.com")) {
    return hostname.split(".")[0];
  }
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return localStorage.getItem("subdomain") || "duntower";
  }
  return null;
}

/** Base URL del API (sin barra final). Subdominio *-test → api2-test. */
export function getWencesApiBaseUrl() {
  return resolveWencesApiBaseUrl();
}

/** Respuesta servida desde caché: el interceptor de error la reconvierte en éxito. */
function rejectWithCachedResponse(config, data) {
  return Promise.reject({
    response: {
      data,
      status: 200,
      statusText: "OK",
      headers: {},
      config,
      request: {},
    },
    isCached: true,
  });
}

const wencesApi = axios.create({
  baseURL: getWencesApiBaseUrl() + "/",
});

function getWencesRequestLanguageHeader() {
  try {
    const token = localStorage.getItem("token");
    const hasToken = token && String(token).length >= 10;
    if (!hasToken) return "es";
    return localStorage.getItem("language") || "es";
  } catch (e) {
    return "es";
  }
}

wencesApi.interceptors.request.use((config) => {
  // *-test.delegat.es siempre habla con api2-test (no con api2 prod del .env.production)
  config.baseURL = getWencesApiBaseUrl() + "/";

  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const subdomain = getCurrentClientDbName() || "";

  // Añadir el subdominio a las cabeceras
  if (subdomain) {
    config.headers["X-Subdomain"] = subdomain;
  }

  const explicitLang =
    config.headers?.savedLanguage || config.headers?.savedlanguage;
  config.headers["savedLanguage"] =
    explicitLang != null && String(explicitLang).trim()
      ? String(explicitLang).trim()
      : getWencesRequestLanguageHeader();

  // Lista completa de definiciones: localStorage antes que la red, y una sola petición
  // cuando varias piden lo mismo a la vez. Ver utils/modelDefinitionCache.js.
  if (
    config.method === "get" &&
    config.responseType !== "blob" &&
    isModelDefinitionsListUrl(config.url)
  ) {
    const cached = readCachedModelDefinitions();
    if (cached) {
      return rejectWithCachedResponse(config, cached);
    }

    // Ya hay una igual en vuelo: esperamos su resultado en vez de duplicarla.
    const pending = getInflightModelDefinitions();
    if (pending) {
      return pending.then(
        (data) => rejectWithCachedResponse(config, data),
        // Si la que salió falló, esta sigue su camino normal y reintenta.
        () => config
      );
    }

    // La marca de "en vuelo" NO va acá: más abajo puede responder el caché de 2 s y
    // entonces la petición nunca sale a la red, el interceptor de respuesta nunca corre
    // y la marca queda colgada. Se marca al final, cuando ya sabemos que va a salir.
    config.marcarDefinicionesEnVuelo = true;
  }

  // Definición de un modelo suelto: si ya tenemos la lista completa, sale de ahí. Es la
  // misma entrada más `redirect: false`; ver utils/modelDefinitionCache.js.
  if (config.method === "get" && config.responseType !== "blob") {
    const modelName = getModelDefinitionName(config.url);
    if (modelName) {
      const derived = findInCachedModelDefinitions(modelName);
      if (derived) {
        return rejectWithCachedResponse(config, derived);
      }

      // La lista está saliendo ahora: esperarla evita una segunda ola de llamadas.
      const listInFlight = getInflightModelDefinitions();
      if (listInFlight) {
        return listInFlight.then(
          (list) => {
            const fromList = deriveModelDefinitionFromList(list, modelName);
            return fromList ? rejectWithCachedResponse(config, fromList) : config;
          },
          () => config
        );
      }
    }
  }

  // Lecturas puras que la pantalla pide repetidas a la vez (settings, rol, listados):
  // sale una y las simultáneas esperan su respuesta. Ver utils/inflightRequestDedupe.js.
  const dedupeKey = getDedupeKey(config);
  if (dedupeKey) {
    const pendingRequest = getInflightRequest(dedupeKey);
    if (pendingRequest) {
      return pendingRequest.then(
        (data) => rejectWithCachedResponse(config, data),
        // Si la que salió falló, esta sigue su camino normal y reintenta.
        () => config
      );
    }

    // Igual que arriba: marcar recién cuando sepamos que sale a la red.
    config.dedupeKeyPendiente = dedupeKey;
  }

  // Verificar caché para peticiones GET (no binarios: evita meter backups en memoria)
  if (config.method === "get" && config.responseType !== "blob") {
    // Construir la URL completa con parámetros para la clave del caché
    let fullUrl = config.url;
    if (config.params && Object.keys(config.params).length > 0) {
      const params = new URLSearchParams(config.params);
      fullUrl = `${config.url}?${params.toString()}`;
    }

    const cachedResponse = getCachedResponse(fullUrl);
    if (cachedResponse) {
      // Crear una respuesta simulada con datos cacheados
      const cachedAxiosResponse = {
        data: cachedResponse,
        status: 200,
        statusText: "OK",
        headers: {},
        config: config,
        request: {},
      };
      // Rechazar la petición para que axios use la respuesta cacheada
      return Promise.reject({
        response: cachedAxiosResponse,
        isCached: true,
      });
    }
  }

  // Recién acá la petición sale de verdad a la red, así que es el único punto seguro
  // para marcarla como "en vuelo": si se marcara antes, una respuesta servida desde
  // caché dejaría la marca sin resolver y las siguientes esperarían al timeout.
  if (config.marcarDefinicionesEnVuelo) {
    config.isModelDefinitionsList = true;
    markInflightModelDefinitions();
  }
  if (config.dedupeKeyPendiente) {
    config.dedupeKey = config.dedupeKeyPendiente;
    markInflightRequest(config.dedupeKey);
  }

  return config;
});

// Interceptor para manejar errores
wencesApi.interceptors.response.use(
  (response) => {
    // Guardar respuesta en cache solo para peticiones GET
    if (response.config.method === "get" && response.config.responseType !== "blob") {
      if (response.config.dedupeKey) {
        settleInflightRequest(response.config.dedupeKey, response.data);
      }

      if (response.config.isModelDefinitionsList) {
        writeCachedModelDefinitions(response.data);
        settleInflightModelDefinitions(response.data);
      }

      // Construir la URL completa con parámetros para la clave del caché
      let fullUrl = response.config.url;
      if (
        response.config.params &&
        Object.keys(response.config.params).length > 0
      ) {
        const params = new URLSearchParams(response.config.params);
        fullUrl = `${response.config.url}?${params.toString()}`;
      }

      apiCache.set(fullUrl, {
        data: response.data,
        timestamp: Date.now(),
      });
    } else if (["post", "put", "patch", "delete"].includes(response.config.method.toLowerCase())) {
      // Limpiar caché automáticamente cuando se hacen operaciones de escritura
      const url = response.config.url;

      // Write-through: quien edita una definición no espera al próximo deploy para verla.
      if (url && url.includes("/api/gotrading/model-definitions")) {
        clearModelDefinitionCache();
      }

      // Tocar una nota comercial (o su aplicación) cambia lo que muestran los renglones
      // de gastos. Ese caché es del módulo y vive toda la sesión: sin esto, el importe
      // en verde seguía siendo el de antes de editar.
      if (url && /notacomercial|nota-comercial/i.test(url)) {
        import("@/modules/nota_comercial_module/utils/notaComercialByIds")
          .then(({ clearNotaComercialByIdCache }) => clearNotaComercialByIdCache())
          .catch(() => {});
      }
      
      // Limpiar la URL específica
      clearCacheForUrl(url);
      
      // Limpiar URLs relacionadas (listados que podrían incluir este item)
      if (url) {
        const urlWithoutParams = url.split('?')[0];
        const urlParts = urlWithoutParams.split('/').filter(part => part);
        
        // Detectar diferentes patrones de URLs y limpiar los listados relacionados
        
        // Patrón 1: /api/gotrading/dynamic/{modelName}/{itemId}
        // Limpiar: /api/gotrading/dynamic/{modelName} (listado)
        const dynamicModelPattern = /^api\/gotrading\/dynamic\/([^\/]+)\/([^\/]+)$/;
        const dynamicMatch = urlWithoutParams.match(dynamicModelPattern);
        if (dynamicMatch) {
          const modelName = dynamicMatch[1];
          const baseUrl = `/api/gotrading/dynamic/${modelName}`;
          clearCacheForUrl(baseUrl);
        }
        
        // Patrón 2: URLs con ID al final (ObjectId de MongoDB: 24 caracteres hex)
        const lastPart = urlParts[urlParts.length - 1];
        if (lastPart && lastPart.length === 24 && /^[0-9a-fA-F]{24}$/.test(lastPart)) {
          // Es un ID, limpiar la ruta base (sin el ID)
          const baseUrl = '/' + urlParts.slice(0, -1).join('/');
          clearCacheForUrl(baseUrl);
          
          // También limpiar variaciones con query params
          clearCacheForUrl(baseUrl + '?');
        }
        
        // Patrón 3: Limpiar rutas padre comunes
        // Si es /api/websites/{id}/pages/{pageId}, limpiar /api/websites/{id}/pages
        if (urlParts.length >= 4) {
          const parentUrl = '/' + urlParts.slice(0, -1).join('/');
          clearCacheForUrl(parentUrl);
        }
        
        // Patrón 4: Para cualquier URL, limpiar también la ruta base sin el último segmento
        if (urlParts.length >= 2) {
          const basePath = '/' + urlParts.slice(0, -1).join('/');
          clearCacheForUrl(basePath);
        }
      }
    }
    return response;
  },
  (error) => {
    // Si es una respuesta cacheada, devolverla como respuesta exitosa
    if (error.isCached && error.response) {
      return error.response;
    }

    // Si falló la petición que las demás estaban esperando, hay que avisarles:
    // si no, se quedan colgadas para siempre.
    if (error?.config?.dedupeKey) {
      settleInflightRequest(error.config.dedupeKey, null, error);
    }

    if (error?.config?.isModelDefinitionsList) {
      settleInflightModelDefinitions(null, error);
    }

    if (error.response && error.response.status === 401) {
      // Canjear un enlace de acceso: el 401 habla del enlace (usado, vencido, inventado),
      // no de la sesión de quien lo abrió. Cerrarle la sesión al admin que pegó un enlace
      // ya quemado es castigarlo por un error ajeno al login que tiene abierto.
      if (error.config?.esCanjeDeEnlace) {
        return Promise.reject(error);
      }

      const currentPath =
        router?.currentRoute?.path ||
        window.location.pathname ||
        "";
      const isPublicAuthPath = ["/login", "/register", "/recoverpassword"].some(
        (publicPath) => currentPath.startsWith(publicPath)
      );
      const failedUrl = error?.config?.url || "(unknown-url)";
      console.warn("[AUTH DEBUG] 401 intercepted", {
        failedUrl,
        currentPath,
        isPublicAuthPath,
      });

      // Limpiar localStorage y redirigir al login
      localStorage.clear();

      // En rutas públicas de auth no forzamos redirect automático.
      // Esto evita que cualquier request secundaria rompa /register.
      if (isPublicAuthPath) {
        return Promise.reject(error);
      }

      // Verificar si el router está disponible antes de usarlo
      if (router && router.push) {
        console.warn("[AUTH DEBUG] redirecting to /login from interceptor");
        router.push("/login");
      } else {
        // Fallback: redirigir usando window.location
        console.warn(
          "[AUTH DEBUG] router unavailable, using window.location /login"
        );
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Función para obtener respuesta cacheada si está disponible y es reciente
const getCachedResponse = (url) => {
  const cached = apiCache.get(url);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

// Función para limpiar el caché manualmente
const clearCache = () => {
  apiCache.clear();
};

// Función para limpiar una URL específica del caché
const clearCacheForUrl = (url) => {
  if (!url) return;
  
  // Normalizar la URL (sin query params para comparación)
  const urlWithoutParams = url.split('?')[0];
  
  // Buscar y eliminar todas las variaciones de la URL (con y sin parámetros)
  const keysToDelete = [];
  for (const [key] of apiCache) {
    const keyWithoutParams = key.split('?')[0];
    
    // Coincidencia exacta o si la URL base coincide
    if (key === url || 
        key.startsWith(url) || 
        keyWithoutParams === urlWithoutParams ||
        keyWithoutParams.startsWith(urlWithoutParams + '/')) {
      keysToDelete.push(key);
    }
  }
  
  // Eliminar todas las claves encontradas
  keysToDelete.forEach(key => {
    apiCache.delete(key);
  });
};

// Función para hacer peticiones con cache
const apiRequest = async (config) => {
  const url = config.url;
  const cachedResponse = getCachedResponse(url);

  if (cachedResponse) {
    return { data: cachedResponse };
  }

  return wencesApi(config);
};

/** Cabeceras JSON con token, tenant e idioma (mismo criterio que el interceptor). */
export function getWencesApiAuthHeaders() {
  const token = localStorage.getItem("token");
  const subdomain = getCurrentClientDbName() || "";
  const headers = {
    "Content-Type": "application/json",
    savedLanguage: getWencesRequestLanguageHeader(),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (subdomain) {
    headers["X-Subdomain"] = subdomain;
  }
  return headers;
}

export { wencesApi, apiRequest, clearCache, clearCacheForUrl, apiCache };
